/**
 * Co-parentage — Frais partagés (port `vCpFrais` proto l. 2953-3007).
 *
 * Phase 1, incrément 16. Vue dédiée aux dépenses partagées entre les
 * 2 parents avec **calcul automatique du solde en temps réel**
 * (CLAUDE.md §2.3). La logique pure vit dans `expenses.ts`
 * (testée 100 % à part).
 *
 * Composition (fidèle au proto) :
 *   - **Card balance** (rendue uniquement si total > 0) : 3 colonnes
 *     (parentA / total / parentB) + libellé « X doit Y € à Z » ou
 *     « ✅ Équilibré » selon la balance.
 *   - **Bouton + Ajouter une dépense** → ouvre le modal.
 *   - **Liste des dépenses** triée par date desc, avec icône catégorie,
 *     description, date+payé par+catégorie, montant, croix supprimer.
 *   - Empty state si aucune dépense.
 */
import { html, type TemplateResult } from 'lit-html';
import { Icon, Icons } from '../../ui/icons.js';
import styles from './FraisView.module.css';
import { getLang, t } from '../../i18n/index.js';
import { addExpense, getCurrentHome, removeExpense } from '../../stores/homes.js';
import { closeModal, getModal, openModal } from '../../stores/ui.js';
import { modalTemplate } from '../../ui/Modal.js';
import { confirmDialog } from '../../ui/Dialog.js';
import { interpolate } from '../ai-engine/logic.js';
import {
  EXPENSE_CATEGORY_ICONS,
  computeBalance,
  formatAmount,
  type BalanceResult,
} from './expenses.js';
import { formatIsoShort, todayIso } from '../../lib/date.js';
import { parentNameByCode } from './custody.js';
import { editorialHero } from '../../ui/Hero.js';
import type {
  CoparentingHomeData,
  CustodyConfig,
  Expense,
  ExpenseCategory,
} from '@nestor/shared-types';

const MODAL_ADD = 'cp-frais:add';

const CATEGORY_KEYS: Record<ExpenseCategory, string> = {
  santé: 'cp_frais_cat_sante',
  éducation: 'cp_frais_cat_education',
  vêtements: 'cp_frais_cat_vetements',
  loisirs: 'cp_frais_cat_loisirs',
  transport: 'cp_frais_cat_transport',
  autre: 'cp_frais_cat_autre',
};

interface FormState {
  description: string;
  amount: string;
  date: string;
  category: ExpenseCategory;
  paidBy: 'A' | 'B';
}

const formState: FormState = {
  description: '',
  amount: '',
  date: todayIso(),
  category: 'autre',
  paidBy: 'A',
};

function categoryLabel(cat: ExpenseCategory): string {
  return t(CATEGORY_KEYS[cat] as Parameters<typeof t>[0]);
}

// ── Balance card ───────────────────────────────────────────────────

function balanceCardTemplate(
  balance: BalanceResult,
  custody: CustodyConfig | null,
): TemplateResult {
  if (balance.total <= 0) return html``;
  const cardClass = balance.balanced
    ? `${styles.balanceCard} ${styles.balanceCardBalanced}`
    : styles.balanceCard;
  const owesText = balance.balanced
    ? t('cp_frais_balanced')
    : interpolate(t('cp_frais_owes_template'), {
        from: parentNameByCode(balance.from ?? undefined, custody),
        to: parentNameByCode(balance.to ?? undefined, custody),
        amount: formatAmount(balance.amount, getLang()),
      });
  const owesClass = balance.balanced
    ? `${styles.balanceText} ${styles.balanceTextBalanced}`
    : styles.balanceText;
  return html`<div class=${cardClass} data-testid="cp-frais-balance-card">
    <div class=${styles.balanceTotals}>
      <div class=${styles.balanceCol}>
        <div class=${styles.balanceLabel}>${custody?.parentA ?? 'Parent A'}</div>
        <div
          class=${`${styles.balanceAmount} ${styles.balanceAmountA}`}
          data-testid="cp-frais-total-a"
        >
          ${formatAmount(balance.totalA, getLang())}
        </div>
      </div>
      <div class=${`${styles.balanceCol} ${styles.balanceColMid}`}>
        <div class=${styles.balanceLabel}>${t('cp_frais_balance_total')}</div>
        <div class=${styles.balanceAmount} data-testid="cp-frais-total">
          ${formatAmount(balance.total, getLang())}
        </div>
      </div>
      <div class=${styles.balanceCol}>
        <div class=${styles.balanceLabel}>${custody?.parentB ?? 'Parent B'}</div>
        <div
          class=${`${styles.balanceAmount} ${styles.balanceAmountB}`}
          data-testid="cp-frais-total-b"
        >
          ${formatAmount(balance.totalB, getLang())}
        </div>
      </div>
    </div>
    <div class=${owesClass} data-testid="cp-frais-owes">${owesText}</div>
  </div>`;
}

// ── Expense row ────────────────────────────────────────────────────

function expenseRowTemplate(
  expense: Expense,
  homeId: string,
  custody: CustodyConfig | null,
): TemplateResult {
  const icon = EXPENSE_CATEGORY_ICONS[expense.category] ?? 'euro';
  const paidByName = parentNameByCode(expense.paidBy, custody);
  const onRemove = (): void => {
    // D7 : une dépense partagée ne part plus sur un clic sec.
    void confirmDialog({
      title: t('cp_frais_remove'),
      message: expense.description,
      confirmLabel: t('remove'),
      danger: true,
    }).then((ok) => {
      if (ok) removeExpense(homeId, expense.id);
    });
  };
  return html`<div class=${styles.row} data-testid="cp-frais-row-${expense.id}">
    <div class=${styles.rowIcon}>${Icon(Icons[icon], 18)}</div>
    <div class=${styles.rowMain}>
      <div class=${styles.rowDescription}>${expense.description}</div>
      <div class=${styles.rowMeta}>
        ${formatIsoShort(expense.date, getLang())} · ${paidByName} ·
        ${categoryLabel(expense.category)}
      </div>
    </div>
    <div class=${styles.rowAmount}>${formatAmount(expense.amount, getLang())}</div>
    <button
      type="button"
      class=${styles.removeBtn}
      data-testid="cp-frais-remove-${expense.id}"
      aria-label=${t('cp_frais_remove')}
      @click=${onRemove}
    >
      ×
    </button>
  </div>`;
}

// ── Modal form ─────────────────────────────────────────────────────

function modalContentTemplate(homeId: string, custody: CustodyConfig | null): TemplateResult {
  const onSubmit = (e: Event): void => {
    e.preventDefault();
    const description = formState.description.trim();
    const amount = parseFloat(formState.amount);
    if (!description || !Number.isFinite(amount) || amount <= 0) return;
    addExpense(homeId, {
      description,
      amount,
      category: formState.category,
      paidBy: formState.paidBy,
      date: formState.date,
    });
    formState.description = '';
    formState.amount = '';
    closeModal();
  };
  return html`<form @submit=${onSubmit}>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_frais_label_description')}</label>
      <input
        class=${styles.formInput}
        type="text"
        data-testid="cp-frais-form-description"
        placeholder=${t('cp_frais_placeholder_description')}
        .value=${formState.description}
        @input=${(e: Event): void => {
          formState.description = (e.target as HTMLInputElement).value;
        }}
        required
      />
    </div>
    <div class=${styles.formGroupRow}>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_frais_label_amount')}</label>
        <input
          class=${styles.formInput}
          type="number"
          step="0.01"
          min="0.01"
          data-testid="cp-frais-form-amount"
          .value=${formState.amount}
          @input=${(e: Event): void => {
            formState.amount = (e.target as HTMLInputElement).value;
          }}
          required
        />
      </div>
      <div class=${styles.formGroup}>
        <label class=${styles.formLabel}>${t('cp_frais_label_date')}</label>
        <input
          class=${styles.formInput}
          type="date"
          data-testid="cp-frais-form-date"
          .value=${formState.date}
          @input=${(e: Event): void => {
            formState.date = (e.target as HTMLInputElement).value;
          }}
        />
      </div>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_frais_label_category')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cp-frais-form-category"
        .value=${formState.category}
        @change=${(e: Event): void => {
          formState.category = (e.target as HTMLSelectElement).value as ExpenseCategory;
        }}
      >
        ${(Object.keys(CATEGORY_KEYS) as ExpenseCategory[]).map(
          (cat) => html`<option value=${cat}>${categoryLabel(cat)}</option>`,
        )}
      </select>
    </div>
    <div class=${styles.formGroup}>
      <label class=${styles.formLabel}>${t('cp_frais_label_paidby')}</label>
      <select
        class=${styles.formSelect}
        data-testid="cp-frais-form-paidby"
        .value=${formState.paidBy}
        @change=${(e: Event): void => {
          formState.paidBy = (e.target as HTMLSelectElement).value as 'A' | 'B';
        }}
      >
        <option value="A">${custody?.parentA ?? 'Parent A'}</option>
        <option value="B">${custody?.parentB ?? 'Parent B'}</option>
      </select>
    </div>
    <button type="submit" class=${styles.formSubmit} data-testid="cp-frais-form-submit">
      ${t('cp_frais_btn_add')}
    </button>
  </form>`;
}

export function renderCpFraisView(): TemplateResult {
  const home = getCurrentHome();
  if (!home || home.mode !== 'coparenting') {
    return html`<section class=${styles.view} data-testid="view-cp-frais">
      <p class=${styles.empty}>${t('cp_home_no_custody')}</p>
    </section>`;
  }
  const data = home.data as CoparentingHomeData;
  const expenses = data.frais.slice().sort((a, b) => b.date.localeCompare(a.date));
  const balance = computeBalance(expenses);
  const onAddClick = (): void => {
    formState.description = '';
    formState.amount = '';
    formState.date = todayIso();
    openModal(MODAL_ADD);
  };
  const showModal = getModal() === MODAL_ADD;

  return html`
    <section class=${styles.view} data-testid="view-cp-frais">
      ${editorialHero({
        badge: { mode: 'coparenting', label: home.name },
        dateLine: t('cp_frais_dateline'),
        title: t('cp_frais_hero_title'),
        accent: t('cp_frais_hero_accent'),
      })}
      ${balanceCardTemplate(balance, data.custody)}
      <button
        type="button"
        class=${styles.addBtn}
        data-testid="cp-frais-add-btn"
        @click=${onAddClick}
      >
        ${t('cp_frais_btn_add')}
      </button>
      ${expenses.length === 0
        ? html`<div class=${styles.empty} data-testid="cp-frais-empty">${t('cp_frais_empty')}</div>`
        : html`<div class=${styles.list} data-testid="cp-frais-list">
            ${expenses.map((e) => expenseRowTemplate(e, home.id, data.custody))}
          </div>`}
      ${showModal
        ? modalTemplate({
            title: t('cp_frais_modal_title'),
            content: modalContentTemplate(home.id, data.custody),
            onClose: closeModal,
            testid: 'cp-frais-modal',
          })
        : html``}
    </section>
  `;
}

/** @internal pour tests : reset le formulaire. */
export function _resetCpFraisFormForTests(): void {
  formState.description = '';
  formState.amount = '';
  formState.date = todayIso();
  formState.category = 'autre';
  formState.paidBy = 'A';
}
